import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, Heart, Users, Target, BookOpen, Clock, Award, CheckCircle, ArrowRight, Lightbulb, TrendingUp, Shield } from 'lucide-react';

export default function AboutPage() {
  useEffect(() => { document.title = 'Om H.J.Ä.L.P. | KBT-program'; }, []);
  const kbtPrinciples = [
    {
      icon: Brain,
      title: 'Kognitiv förändring',
      description: 'Identifiera och förändra negativa tankemönster som påverkar känslor och beteenden.'
    },
    {
      icon: Target,
      title: 'Beteendeförändring',
      description: 'Utveckla nya, hälsosamma beteenden genom strukturerade övningar och tekniker.'
    },
    {
      icon: Heart,
      title: 'Känsloreglering',
      description: 'Lär dig hantera och reglera känslor på ett konstruktivt sätt.'
    },
    {
      icon: Users,
      title: 'Relationsförbättring',
      description: 'Förbättra kommunikation och relationer genom KBT-tekniker.'
    }
  ];

  const courseLevels = [
    {
      level: 'Modul 1',
      duration: '12 sessioner',
      modules: 12,
      description: 'Helhetsorienterande förståelse — grunden för förändring',
      topics: [
        'Introduktion till programmet och dina mål',
        'Förstå tankens kraft och kognitiva mönster',
        'Identifiera egna beteendemönster',
        'Känsloreglering och medvetenhet',
        'Relationer och kommunikation',
        'Riskbedömning och styrkor'
      ],
      suitable: 'Alla deltagare — obligatorisk startmodul'
    },
    {
      level: 'Modul 2',
      duration: '12 sessioner',
      modules: 12,
      description: 'Justeringar av kognitiva mönster — omstrukturera ditt tänkande',
      topics: [
        'Identifiera och utmana negativa tankefällor',
        'Kognitiv omstrukturering i praktiken',
        'Hantera impulsivitet och reaktioner',
        'Motivationsarbete och förändringssteg',
        'Problemlösningsstrategier',
        'Återfallsprevention och planering'
      ],
      suitable: 'Deltagare som slutfört Modul 1'
    },
    {
      level: 'Modul 3',
      duration: '12 sessioner',
      modules: 12,
      description: 'Ändring av livsstrategier — bygg nya vanor och strukturer',
      topics: [
        'Strukturera vardagen för långsiktig stabilitet',
        'Socialt nätverk och prosociala relationer',
        'Arbete, utbildning och meningsfull sysselsättning',
        'Hantera stress och belastning',
        'Ekonomi och ansvar',
        'Stärka självkänsla och identitet'
      ],
      suitable: 'Deltagare som slutfört Modul 2'
    },
    {
      level: 'Modul 4',
      duration: '12 sessioner',
      modules: 12,
      description: 'Praktiskt stöd för återintegration — framtiden börjar nu',
      topics: [
        'Plan för livet efter programmet',
        'Hantera motgångar och kriser',
        'Stärka skyddsfaktorer',
        'Familj och nätverksrelationer',
        'Samhällsdeltagande och rättigheter',
        'Utvärdering, certifiering och avslut'
      ],
      suitable: 'Deltagare som slutfört Modul 3'
    }
  ];

  const benefits = [
    {
      icon: Lightbulb,
      title: 'Evidensbaserad metod',
      description: 'KBT är en av de mest vetenskapligt undersökta och beprövade terapiformerna.'
    },
    {
      icon: TrendingUp,
      title: 'Mätbara resultat',
      description: 'Strukturerad approach som möjliggör uppföljning och mätning av framsteg.'
    },
    {
      icon: Clock,
      title: 'Strukturerad metod',
      description: 'Tydlig struktur med 48 sessioner fördelade på 4 moduler.'
    },
    {
      icon: Shield,
      title: 'Långsiktig effekt',
      description: 'Ger verktyg för självhjälp och förebyggande av återfall.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-95"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Badge variant="secondary" className="mb-6 bg-white/20 text-white border-white/30">
              🧠 Kognitiv Beteendeterapi — Pedagogiskt stödprogram
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Om <span className="text-primary-glow">H.J.Ä.L.P.</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              H.J.Ä.L.P. är ett KBT-baserat stödprogram med 48 sessioner och 4 moduler —
              ett pedagogiskt verktyg för personlig förändring och återintegration.
            </p>
            <p className="text-sm text-white/60 mb-6">
              H.J.Ä.L.P. är ett pedagogiskt stödprogram — inte hälso- och sjukvård.
            </p>
            <Button size="lg" asChild className="text-lg px-8 py-6 bg-white text-primary hover:bg-white/90">
              <Link to="/register">
                Ansök till H.J.Ä.L.P.
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What is KBT Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Vad är Kognitiv Beteendeterapi?
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                KBT är en strukturerad, målinriktad och evidensbaserad metod
                som fokuserar på sambandet mellan tankar, känslor och beteenden.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {kbtPrinciples.map((principle, index) => (
                <Card key={index} className="shadow-elegant hover:shadow-glow transition-all duration-300 bg-gradient-card border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center">
                        <principle.icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-xl">{principle.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {principle.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="shadow-elegant bg-gradient-card border-0">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  KBT:s kärnprinciper
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-2" />
                      Här och nu-fokus
                    </h4>
                    <p className="text-muted-foreground">
                      Koncentrerar sig på nuvarande problem och lösningar snarare än att gräva i det förflutna.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-2" />
                      Strukturerad approach
                    </h4>
                    <p className="text-muted-foreground">
                      Använder tydliga mål, agenda och hemuppgifter för att maximera framstegen.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-2" />
                      Samarbete
                    </h4>
                    <p className="text-muted-foreground">
                      Handledare och deltagare arbetar tillsammans som ett team mot gemensamma mål.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-2" />
                      Färdighetsträning
                    </h4>
                    <p className="text-muted-foreground">
                      Lär ut konkreta verktyg och tekniker som kan användas i vardagen.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Course Levels Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                H.J.Ä.L.P.-programmets 4 moduler
              </h2>
              <p className="text-xl text-muted-foreground">
                48 sessioner fördelade på 4 progressiva moduler — från grundläggande förståelse till återintegration.
              </p>
            </div>
            
            <div className="space-y-8">
              {courseLevels.map((course, index) => (
                <Card key={index} className="shadow-elegant hover:shadow-glow transition-all duration-300 bg-gradient-card border-0">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-4 mb-2">
                          <Badge variant="secondary" className="text-sm">
                            {course.level}
                          </Badge>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 mr-1" />
                            {course.duration}
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <BookOpen className="h-4 w-4 mr-1" />
                            {course.modules} moduler
                          </div>
                        </div>
                        <CardTitle className="text-2xl">{course.level} i KBT</CardTitle>
                        <CardDescription className="text-base mt-2">
                          {course.description}
                        </CardDescription>
                      </div>
                      <Button asChild className="mt-4 md:mt-0">
                        <Link to="/register">
                          Läs mer
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Kursinnehåll:</h4>
                        <ul className="space-y-2">
                          {course.topics.map((topic, topicIndex) => (
                            <li key={topicIndex} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Lämplig för:</h4>
                        <p className="text-muted-foreground">{course.suitable}</p>
                        
                        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="flex items-center">
                              <Award className="h-4 w-4 text-primary mr-1" />
                              Certifierat
                            </div>
                            <div className="flex items-center">
                              <Users className="h-4 w-4 text-primary mr-1" />
                              Max 15 deltagare
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Fördelar med KBT
              </h2>
              <p className="text-xl text-muted-foreground">
                Varför KBT är en av de mest effektiva och väldokumenterade metoderna för beteendeförändring.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="shadow-elegant hover:shadow-glow transition-all duration-300 bg-gradient-card border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center">
                        <benefit.icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Redo att ta nästa steg med H.J.Ä.L.P.?
            </h2>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Börja din resa mot förändring med ett strukturerat KBT-baserat stödprogram.
              48 sessioner med tydliga mål, verktyg och handledning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-lg px-8 py-6 bg-white text-primary hover:bg-white/90">
                <Link to="/register">
                  Ansök till H.J.Ä.L.P.
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="secondary" asChild className="text-lg px-8 py-6">
                <Link to="/contact">
                  Kontakta oss
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}